import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { STAFF_ALERT_SMS_DELAY_MAX, STAFF_ALERT_SMS_DELAY_MIN, resolveStaffAlertSettings } from '@his/shared';
import { get, put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { useTranslation } from '@/lib/i18n';
import { Card, CardContent, CardHeader, CardTitle, Checkbox, Field, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

interface StaffAlertSettingsPatch {
  staffAlertSmsEscalation?: boolean;
  staffAlertSmsDelayMinutes?: number;
}

export function StaffAlertSmsCard({ readOnly }: { readOnly: boolean }) {
  const { t } = useTranslation('notifications');
  const toast = useToast();
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: () => get<{ settings: Record<string, unknown> }>('/org/settings'),
  });
  const current = resolveStaffAlertSettings(settings.data?.settings);

  const save = useMutation({
    mutationFn: (patch: StaffAlertSettingsPatch) => put('/org/settings', patch),
    onSuccess: () => {
      toast.success(t('staff_alert.settings.saved'));
      void queryClient.invalidateQueries({ queryKey: ['tenant-settings'] });
    },
    onError: (error) => toast.error(t('staff_alert.settings.could_not_save'), describeError(error)),
  });

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-sm">{t('staff_alert.settings.title')}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <Checkbox
          className="sm:col-span-2"
          checked={current.smsEscalation}
          disabled={readOnly || save.isPending || !settings.data}
          onCheckedChange={(checked) => save.mutate({ staffAlertSmsEscalation: checked })}
          label={t('staff_alert.settings.enable')}
        />
        <Field label={t('staff_alert.settings.delay')} hint={t('staff_alert.settings.hint')} className="sm:col-span-2">
          <Input
            type="number"
            className="max-w-32"
            min={STAFF_ALERT_SMS_DELAY_MIN}
            max={STAFF_ALERT_SMS_DELAY_MAX}
            disabled={readOnly || !current.smsEscalation}
            defaultValue={current.smsDelayMinutes}
            key={current.smsDelayMinutes}
            onBlur={(event) => {
              const next = Number(event.target.value);
              if (Number.isInteger(next) && next >= STAFF_ALERT_SMS_DELAY_MIN && next <= STAFF_ALERT_SMS_DELAY_MAX && next !== current.smsDelayMinutes) {
                save.mutate({ staffAlertSmsDelayMinutes: next });
              }
            }}
          />
        </Field>
      </CardContent>
    </Card>
  );
}
